import styled, { css } from 'styled-components';

import { BaseStyle } from '@nhl/nhl-breakaway-ui';

type LinkProps = {
    noLinks: boolean;
    twoLinks: boolean;
    linksWidth?: number;
    dateWidth?: number;
};

const getMinWidthForContainer = (isLinkContainer: boolean, linkProps?: LinkProps) => {
    // When we have a special event, we want to ensure that both the date container and links container are the same
    // size to ensure that the image/text for the special event remains centered.
    const MIN_SIZE = '125px';
    const FIT_CONTENT = 'fit-content';

    if (linkProps) {
        if (isLinkContainer) {
            return linkProps.noLinks ? `${linkProps.dateWidth}px` : linkProps.twoLinks ? FIT_CONTENT : MIN_SIZE;
        }

        return linkProps.noLinks ? FIT_CONTENT : linkProps.twoLinks ? `${linkProps.linksWidth}px` : MIN_SIZE;
    }

    return 'initial';
};

/**
 * Basic Game Info (Date, Network)
 */
export const GameInfo = styled.div`
    display: flex;
    align-items: center;
    padding-bottom: 16px;
    justify-content: space-between;
`;

export const DateNetworkContainer = styled.div<{ $showingBroadcasters: boolean; $linkContainerProps: LinkProps }>`
    display: flex;
    flex-direction: column;

    ${BaseStyle.Mixins.screenSizes.medium} {
        min-width: ${({ $linkContainerProps }) => getMinWidthForContainer(false, $linkContainerProps)};
        max-width: calc(100vw - 200px);
    }

    ${({ $showingBroadcasters }) =>
        $showingBroadcasters &&
        css`
            ${BaseStyle.Mixins.gaps.small}
        `}
`;

export const DateContainer = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['10bCaps']}
    color: ${({ theme }) => theme.Text.secondary};

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.fontStyles.ui['14bCaps']}
    }
`;

export const NetworkContainer = styled.div`
    flex-direction: column;
    align-items: start;
    justify-content: initial;

    ${BaseStyle.Mixins.screenSizes.medium} {
        align-items: center;
        flex-direction: row;
        max-height: 20px;
    }
`;

export const LinksContainer = styled.div<{ $linkContainerProps?: LinkProps }>`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    flex-flow: wrap;
    justify-content: right;

    ${BaseStyle.Mixins.screenSizes.medium} {
        min-width: ${({ $linkContainerProps }) => getMinWidthForContainer(true, $linkContainerProps)};
    }
`;

export const LinkContainer = styled.div`
    display: flex;
    justify-content: end;
`;

export const ScoreboardButton = styled.a`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    min-height: 34px;
    padding: 7px 16px;
    justify-content: center;
    align-items: center;

    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.Surface.border};
    background: ${({ theme }) => theme.Surface.primary};
    cursor: pointer;

    && {
        ${BaseStyle.Mixins.fontStyles.ui['14b']}
        color: ${({ theme }) => theme.Utility.cta};

        &:hover {
            background: ${({ theme }) => theme.Surface.border};
            color: ${({ theme }) => theme.Utility.cta};
        }
    }

    svg {
        fill: ${({ theme }) => theme.Utility.cta};
        height: 20px;
        width: 20px;
    }
`;

export const ScoreBox = styled.div`
    display: inline-flex;
    align-items: center;
    flex: 1;

    ${BaseStyle.Mixins.screenSizes.large} {
        gap: 32px;
    }

    ${BaseStyle.Mixins.screenSizes.extraLarge} {
        gap: 48px;
    }
`;

export const ScoreContainer = styled.div<{ $isLosingScore: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['32b']}
    // this nightmare works in conjunction with the one in TeamNameContainer up above. this keeps the maximum width 
    // for the score number at 40px but lets it shrink a bit just above that 375px breakpoint to prevent overlaps
    width: clamp(28px, calc((100vw - 375px)/2), 40px);
    text-align: center;
    color: ${({ $isLosingScore, theme }) => ($isLosingScore ? theme.Text.secondary : theme.Text.primary)};

    ${BaseStyle.Mixins.screenSizes.small} {
        ${BaseStyle.Mixins.fontStyles.ui['24b']}
    }

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.fontStyles.ui['48b']}
        width: 60px;
    }
`;

export const PeriodTimeContainer = styled.div`
    ${BaseStyle.Mixins.fontStyles.desktop.subtitle1}
    ${BaseStyle.Mixins.gaps.extraSmall}
    
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.gaps.small}
    }
`;

export const SituationDescription = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['10bCaps']}
    ${BaseStyle.Mixins.gaps.small}
    

    display: flex;

    * {
        color: ${({ theme }) => theme.Utility.critical};
    }

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.fontStyles.ui['14bCaps']}
    }
`;
